import { Component, inject, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { RegistrationService } from '../../services/registration.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-email-verification-pending',
  standalone: true,
  imports: [CommonModule, RouterModule, ToastModule, TranslocoModule],
  templateUrl: './email-verification-pending.component.html'
})
export class EmailVerificationPendingComponent implements OnInit, OnDestroy {
  private registrationService = inject(RegistrationService);
  private router = inject(Router);
  private translocoService = inject(TranslocoService);

  // State
  email = signal<string>('');
  isResending = signal(false);
  cooldownSeconds = signal(0);
  message = signal('');
  messageType = signal<'success' | 'error'>('success');

  private cooldownInterval?: number;

  ngOnInit() {
    // Récupérer l'email depuis localStorage ou state
    const storedEmail = localStorage.getItem('pendingVerificationEmail');
    if (storedEmail) {
      this.email.set(storedEmail);
    } else {
      // Si pas d'email, rediriger vers register
      this.router.navigate(['/auth/register']);
    }
  }

  async resendVerification() {
    if (this.isResending() || this.cooldownSeconds() > 0) return;

    this.isResending.set(true);
    this.message.set('');

    try {
      // Utiliser l'endpoint resend-verification du backend
      const response = await fetch(`${environment.apiUrl}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: this.email()
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        this.message.set(this.translocoService.translate('emailVerification.messages.emailSent'));
        this.messageType.set('success');
        this.startCooldown();
      } else {
        this.message.set(result.message || this.translocoService.translate('emailVerification.messages.sendFailed'));
        this.messageType.set('error');
      }
    } catch (error) {
      this.message.set(this.translocoService.translate('emailVerification.messages.networkError'));
      this.messageType.set('error');
    } finally {
      this.isResending.set(false);
    }
  }

  private startCooldown() {
    this.cooldownSeconds.set(60);
    
    this.cooldownInterval = window.setInterval(() => {
      const current = this.cooldownSeconds();
      if (current <= 1) {
        this.cooldownSeconds.set(0);
        if (this.cooldownInterval) {
          clearInterval(this.cooldownInterval);
        }
      } else {
        this.cooldownSeconds.set(current - 1);
      }
    }, 1000);
  }

  goToLogin() {
    localStorage.removeItem('pendingVerificationEmail');
    this.router.navigate(['/auth/login']);
  }

  ngOnDestroy() {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
  }
}